import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
  Backup as BackupIcon,
  Balance as BalanceIcon,
} from '@mui/icons-material';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, ChartTooltip, Legend);

interface ShardStatus {
  shardId: string;
  status: 'healthy' | 'warning' | 'error';
  userCount: number;
  activityCount: number;
  sizeMB: number;
  lastCheck: string;
  responseTime: number;
  errorRate: number;
}

const ShardsManagementPage: React.FC = () => {
  const [shards, setShards] = useState<ShardStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});

  const fetchShardsData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Simulated API call - replace with actual RTK Query endpoint
      // const response = await fetch('/v1/admin/shards');
      // const data = await response.json();
      
      // Mock data for demonstration
      const mockShards: ShardStatus[] = [
        {
          shardId: 'shard-001',
          status: 'healthy',
          userCount: 1250,
          activityCount: 45678,
          sizeMB: 2048,
          lastCheck: new Date().toISOString(),
          responseTime: 45,
          errorRate: 0.01,
        },
        {
          shardId: 'shard-002',
          status: 'healthy',
          userCount: 1180,
          activityCount: 42340,
          sizeMB: 1920,
          lastCheck: new Date().toISOString(),
          responseTime: 52,
          errorRate: 0.02,
        },
        {
          shardId: 'shard-003',
          status: 'warning',
          userCount: 1520,
          activityCount: 58920,
          sizeMB: 2560,
          lastCheck: new Date().toISOString(),
          responseTime: 125,
          errorRate: 0.15,
        },
        {
          shardId: 'shard-004',
          status: 'healthy',
          userCount: 1090,
          activityCount: 38450,
          sizeMB: 1750,
          lastCheck: new Date().toISOString(),
          responseTime: 38,
          errorRate: 0.01,
        },
      ];
      
      setShards(mockShards);
    } catch (err) {
      setError('Failed to fetch shards data. Please try again.');
      console.error('Error fetching shards:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShardsData();
  }, []);

  const handleCheckHealth = async (shardId: string) => {
    setActionLoading({ ...actionLoading, [shardId]: true });
    try {
      // Simulated API call
      // await fetch(`/v1/admin/shards/${shardId}/health`, { method: 'POST' });
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchShardsData();
    } catch (err) {
      console.error(`Error checking health for ${shardId}:`, err);
    } finally {
      setActionLoading({ ...actionLoading, [shardId]: false });
    }
  };

  const handleRebalance = async (shardId: string) => {
    setActionLoading({ ...actionLoading, [`${shardId}-rebalance`]: true });
    try {
      // Simulated API call
      // await fetch(`/v1/admin/shards/${shardId}/rebalance`, { method: 'POST' });
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchShardsData();
    } catch (err) {
      console.error(`Error rebalancing ${shardId}:`, err);
    } finally {
      setActionLoading({ ...actionLoading, [`${shardId}-rebalance`]: false });
    }
  };

  const handleBackup = async (shardId: string) => {
    setActionLoading({ ...actionLoading, [`${shardId}-backup`]: true });
    try {
      // Simulated API call
      // await fetch(`/v1/admin/shards/${shardId}/backup`, { method: 'POST' });
      await new Promise(resolve => setTimeout(resolve, 1500));
      await fetchShardsData();
    } catch (err) {
      console.error(`Error backing up ${shardId}:`, err);
    } finally {
      setActionLoading({ ...actionLoading, [`${shardId}-backup`]: false });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const totalUsers = shards.reduce((sum, shard) => sum + shard.userCount, 0);
  const totalActivities = shards.reduce((sum, shard) => sum + shard.activityCount, 0);
  const totalSize = shards.reduce((sum, shard) => sum + shard.sizeMB, 0);

  const chartData = {
    labels: shards.map(shard => shard.shardId),
    datasets: [
      {
        label: 'User Distribution',
        data: shards.map(shard => shard.userCount),
        backgroundColor: [
          'rgba(13, 71, 161, 0.8)',
          'rgba(25, 118, 210, 0.8)',
          'rgba(66, 165, 245, 0.8)',
          'rgba(144, 202, 249, 0.8)',
        ],
        borderColor: [
          'rgba(13, 71, 161, 1)',
          'rgba(25, 118, 210, 1)',
          'rgba(66, 165, 245, 1)',
          'rgba(144, 202, 249, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = ((value / totalUsers) * 100).toFixed(1);
            return `${label}: ${value} users (${percentage}%)`;
          }
        }
      }
    },
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Database Shards Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchShardsData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {navigator.onLine === false && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are currently offline. Shard management features are limited.
        </Alert>
      )}

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Shards
              </Typography>
              <Typography variant="h4">{shards.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h4">{totalUsers.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Activities
              </Typography>
              <Typography variant="h4">{totalActivities.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Size
              </Typography>
              <Typography variant="h4">{(totalSize / 1024).toFixed(2)} GB</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Shard Status
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Shard ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Users</TableCell>
                      <TableCell align="right">Activities</TableCell>
                      <TableCell align="right">Size (MB)</TableCell>
                      <TableCell align="right">Response Time (ms)</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shards.map((shard) => (
                      <TableRow key={shard.shardId}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <StorageIcon fontSize="small" />
                            {shard.shardId}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(shard.status)}
                            label={shard.status.toUpperCase()}
                            color={getStatusColor(shard.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{shard.userCount.toLocaleString()}</TableCell>
                        <TableCell align="right">{shard.activityCount.toLocaleString()}</TableCell>
                        <TableCell align="right">{shard.sizeMB.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Typography
                            color={shard.responseTime > 100 ? 'error' : 'textPrimary'}
                          >
                            {shard.responseTime}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Check Health">
                              <IconButton
                                size="small"
                                onClick={() => handleCheckHealth(shard.shardId)}
                                disabled={actionLoading[shard.shardId]}
                              >
                                {actionLoading[shard.shardId] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <RefreshIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Rebalance">
                              <IconButton
                                size="small"
                                onClick={() => handleRebalance(shard.shardId)}
                                disabled={actionLoading[`${shard.shardId}-rebalance`]}
                              >
                                {actionLoading[`${shard.shardId}-rebalance`] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <BalanceIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Backup">
                              <IconButton
                                size="small"
                                onClick={() => handleBackup(shard.shardId)}
                                disabled={actionLoading[`${shard.shardId}-backup`]}
                              >
                                {actionLoading[`${shard.shardId}-backup`] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <BackupIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Distribution
              </Typography>
              <Box height={300}>
                <Pie data={chartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ShardsManagementPage;